import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { Map, List, fromJS } from 'immutable'
import compose from 'recompose/compose'
import { connect as connectRedux } from 'react-redux'
import { getBoundActions } from '../../../actions/index'
import { getPrimary } from '../../../services/languages/index'
import { useGetPlanDataBySku } from '../../../services/plans/hooks'
import {
  formatPrice,
  PLAN_SKU_PRICE_INCREASE_2026_ANNUAL_SPECIAL,
} from '../../../services/plans/index'
import { getUserAccountChangePlanProratePrice } from '../../../services/user-account/index'
import { getCurrentTime } from '../../../services/date-time/index'
import {
  CHANGE_PLAN_OFFER_VIEW_GA4_EVENT,
  CHANGE_PLAN_OFFER_ACCEPT_GA4_EVENT,
  CHANGE_PLAN_OFFER_CONFIRM_GA4_EVENT,
} from '../../../services/event-tracking/index'
import Button, { TYPES as BUTTON_TYPES } from '../../Button/index'
import { H1, HEADING_TYPES } from '../../Heading/index'
import CheckIcon from '../../Icon/icons/Check/index'
import PlanValueProp from '../../PlanValueProp/index'
import MyAccountChangePlanModal from '../MyAccountChangePlanModal/index'
import { setSessionStorage } from '../../../services/local-preferences/index'

// Define plan SKUs array as a constant to prevent recreation on each render
const PLAN_SKUS = [PLAN_SKU_PRICE_INCREASE_2026_ANNUAL_SPECIAL]

function CountdownTimer (props) {
  const { staticText } = props
  const [countdown, setCountdown] = useState(null)

  // Countdown timer - use March 2 11pm if before that date, otherwise March 31 midnight
  useEffect(() => {
    const firstPromoEndDate = new Date('2026-03-02T23:00:00')
    const secondPromoEndDate = new Date('2026-03-31T23:59:59')
    const now = getCurrentTime()
    const currentTime = new Date(now).getTime()

    // Determine which end date to use based on current date
    const endDate = currentTime < firstPromoEndDate.getTime()
      ? firstPromoEndDate
      : secondPromoEndDate

    const updateCountdown = () => {
      const nowTime = new Date().getTime()
      const distance = endDate.getTime() - nowTime

      if (distance < 0) {
        setCountdown(null)
        return
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24))
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((distance % (1000 * 60)) / 1000)

      setCountdown({
        days,
        hours,
        minutes,
        seconds,
      })
    }

    updateCountdown()
    const interval = setInterval(updateCountdown, 1000)

    return () => clearInterval(interval)
  }, [])

  if (!countdown) {
    return null
  }

  return (
    <div className="my-account-change-plan-promo-modal__countdown">
      {countdown.days} {staticText.get('days')} {countdown.hours} {staticText.get('hours')} {countdown.minutes} {staticText.get('minutes')} {countdown.seconds} {staticText.get('seconds')} <span className="my-account-change-plan-promo-modal__text-highlight">{staticText.get('left')}</span>
    </div>
  )
}

function PriceSecured (props) {
  const {
    setEventDefault,
  } = props

  // Track GA4 event when PriceSecured component is shown
  useEffect(() => {
    setEventDefault(CHANGE_PLAN_OFFER_CONFIRM_GA4_EVENT)
  }, [])

  return (
    <div className="my-account-change-plan-promo-modal" />
  )
}

function OfferCard (props) {
  const {
    staticText,
    plan,
    initialPrice,
    monthlyPrice,
    recurringPrice,
    onClickAcceptOffer,
    currentPlan,
    isProcessing,
  } = props
  const [showMoreDetails, setShowMoreDetails] = useState(false)

  if (!plan.size) {
    return null
  }

  const planTitle = plan.get('heading')

  // Get value props from plan data
  const valueProps = plan.get('valueProps', List()) || List()
  const firstTwoValueProps = valueProps.take(2)
  const remainingValueProps = valueProps.skip(2)

  return (
    <div className="my-account-change-plan-promo-modal__offer-card">
      <div className="my-account-change-plan-promo-modal__banner">
        {staticText.get('memberAppreciationRate')}
      </div>
      <div className="my-account-change-plan-promo-modal__card-content">
        <div className="my-account-change-plan-promo-modal__plan-content">
          <div className="my-account-change-plan-promo-modal__plan-title">
            {planTitle}
          </div>
          {monthlyPrice && (
            <p className="my-account-change-plan-promo-modal__monthly-equivalent">
              {staticText.get('only')} <span className="my-account-change-plan-promo-modal__offer-highlight">{monthlyPrice}</span> {staticText.get('perMonth')},
            </p>
          )}
          <p className="my-account-change-plan-promo-modal__pricing-details">
            <span className="my-account-change-plan-promo-modal__offer-highlight">{initialPrice}</span> <span className="my-account-change-plan-promo-modal__strikethrough">{recurringPrice}</span> {staticText.get('for')} <span className="my-account-change-plan-promo-modal__offer-highlight">{staticText.get('anotherYear')}</span>,
          </p>
          <p className="my-account-change-plan-promo-modal__pricing-details">
            {`${String(staticText.get('thenBilledAnnually')).replace('${ recurringPrice }', recurringPrice)}`} {/* eslint-disable-line */}
          </p>
          <Button
            className="my-account-change-plan-promo-modal__accept-button"
            type={BUTTON_TYPES.PRIMARY}
            onClick={() => {
              onClickAcceptOffer(currentPlan)
            }}
            disabled={isProcessing}
          >
            {isProcessing ? staticText.get('changePlanButtonProcessing') : staticText.get('acceptOffer')}
          </Button>
        </div>
        <div className="my-account-change-plan-promo-modal__benefits">
          <div className="my-account-change-plan-promo-modal__benefit-items">
            {firstTwoValueProps.map((valueProp, index) => (
              <div key={`value-prop-${index}`} className="my-account-change-plan-promo-modal__benefit">
                <CheckIcon size={32} className="my-account-change-plan-promo-modal__check-icon" />
                <PlanValueProp valueProp={valueProp} className="my-account-change-plan-promo-modal__value-prop" />
              </div>
            ))}
            {showMoreDetails && remainingValueProps.map((valueProp, index) => (
              <div key={`value-prop-remaining-${index}`} className="my-account-change-plan-promo-modal__benefit">
                <CheckIcon size={32} className="my-account-change-plan-promo-modal__check-icon" />
                <PlanValueProp valueProp={valueProp} className="my-account-change-plan-promo-modal__value-prop" />
              </div>
            ))}
          </div>
            {!showMoreDetails && remainingValueProps.size > 0 && (
              <Button
                type={BUTTON_TYPES.LINK}
                className="my-account-change-plan-promo-modal__more-link"
                onClick={() => setShowMoreDetails(true)}
              >
                {staticText.get('clickToSeeMore')}
              </Button>
            )}
        </div>
      </div>
    </div>
  )
}

function AcceptOfferModal (props) {
  const {
    setShowAcceptOfferModal,
    showPlanChanged,
    isProcessing,
  } = props
  const overlayRef = useRef(null)

  const handleClose = () => {
    setShowAcceptOfferModal(false)
  }

  useEffect(() => {
    overlayRef.current?.focus()
  }, [])

  return (
    <div
      ref={overlayRef}
      className="my-account-change-plan-promo-modal__inner-modal-overlay"
      onClick={!showPlanChanged && !isProcessing ? handleClose : null}
      onKeyDown={(e) => e.key === 'Escape' && !showPlanChanged && !isProcessing ? handleClose() : null} // eslint-disable-line no-confusing-arrow
      role="button"
      tabIndex={0}
      aria-label="Close"
    >
      <div
        className="my-account-change-plan-promo-modal__inner-modal"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="accept-offer-inner-modal-title"
      >
        <MyAccountChangePlanModal
          dismissModalOverride={handleClose}
        />
      </div>
    </div>
  )
}

function Loading () {
  return (
    <div className="my-account-change-plan-promo-modal__loading">
      <div className="my-account-change-plan-promo-modal__card-placeholder">
        <div className="my-account-change-plan-promo-modal__card-placeholder-item my-account-change-plan-promo-modal__card-placeholder-item--small" />
        <div className="my-account-change-plan-promo-modal__card-placeholder-item my-account-change-plan-promo-modal__card-placeholder-item--small" />
        <div className="my-account-change-plan-promo-modal__card-placeholder-item my-account-change-plan-promo-modal__card-placeholder-item--large" />
        <div className="my-account-change-plan-promo-modal__card-placeholder-item my-account-change-plan-promo-modal__card-placeholder-item--large" />
        <div className="my-account-change-plan-promo-modal__card-placeholder-item" />
      </div>
    </div>
  )
}

function MyAccountChangePlanPromoModal (props) {
  const {
    staticText,
    dismissModal,
    user,
    auth,
    userAccount,
    resetAccountChangePlanData,
    setEventDefault,
  } = props
  // Local state for prorate price data
  const [proratePriceData, setProratePriceData] = useState(null)
  const [isProratePriceLoading, setIsProratePriceLoading] = useState(false)
  // Local state for inner "accept offer" modal (fake modal within this component)
  const [showAcceptOfferModal, setShowAcceptOfferModal] = useState(false)
  // Ref to track if view event has been fired (to ensure it only fires once)
  const hasTrackedViewEvent = useRef(false)
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const authJwt = auth.get('jwt')
  const userPlanSku = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planSku'])
  const gqlUserPlanData = useGetPlanDataBySku({
    userLanguage,
    authJwt,
    planSkus: userPlanSku,
    forceRefresh: true,
  }) || Map()
  const userPlanData = gqlUserPlanData.get('plans', List()) || List()
  const currentPlan = userPlanData.get(0) || Map()

  // Fetch both plans in a single API call
  const gqlPlanData = useGetPlanDataBySku({
    userLanguage,
    authJwt,
    planSkus: PLAN_SKUS,
  })
  const planData = gqlPlanData.get('plans', List()) || List()
  const planLoading = gqlPlanData.get('loading', false)
  const planComplete = gqlPlanData.get('complete', false)

  // Extract the special offer plan and regular annual plan from the results
  const specialOfferPlan = planData.find((p) => p.get('sku') === PLAN_SKU_PRICE_INCREASE_2026_ANNUAL_SPECIAL) || Map()

  // Extract plan information
  const currency = specialOfferPlan.get('currencyIso')
  const specialOfferInitialCost = (specialOfferPlan.get('costs', List()) || List()).first()
  const specialOfferCost = (specialOfferPlan.get('costs', List()) || List()).last()
  const initialPrice = formatPrice(currency, specialOfferInitialCost, false, null, userLanguage)
  const recurringPrice = formatPrice(currency, specialOfferCost, false, null, userLanguage)

  // Calculate monthly equivalent (divide by 12 for annual plan)
  const monthlyCost = specialOfferInitialCost
    ? (parseFloat(specialOfferInitialCost) / 12).toFixed(2)
    : null
  const monthlyPrice = monthlyCost
    ? formatPrice(currency, monthlyCost, false, null, userLanguage)
    : null

  // Check for plan change success/error states
  const planChangeData = userAccount.getIn(['data', 'billing', 'planChange'])
  // Check processing flags - if processing, show processing state even if planChangeData exists
  // (this handles retry scenarios where error data still exists)
  const billingProcessing = userAccount.getIn(['billingSubscriptionsProcessing'])
  const authProcessing = auth.get('processing')
  const isProcessing = billingProcessing || authProcessing
  // Match reference component logic exactly
  // Check if planChangeData exists and is a valid Map before accessing properties
  const hasPlanChangeData = Map.isMap(planChangeData) && planChangeData.size > 0
  const showChangeError = hasPlanChangeData && (
    planChangeData.get('_dataError')
    || planChangeData.get('success') === false
  )
  // Match reference component: if planChange exists and no error, show success
  const showPlanChanged = hasPlanChangeData && !showChangeError

  const onClickChangePlanConfirm = () => {
    const { setPlanChangeSelected } = props
    // Track GA4 event when accept offer button is clicked
    setEventDefault(CHANGE_PLAN_OFFER_ACCEPT_GA4_EVENT)
    setPlanChangeSelected(specialOfferPlan)
  }

  useEffect(() => {
    // Reset the account change plan data when the component unmounts
    return () => {
      resetAccountChangePlanData()
    }
  }, [])

  // Track GA4 event when modal is shown (after loading completes and plan data is displayed)
  // Only fire once when content is actually rendered (not loading state)
  useEffect(() => {
    // Check if we should show content (not loading) and haven't tracked yet
    const shouldShowContent = !showPlanChanged
      && !showChangeError
      && !planLoading
      && planComplete
      && !isProratePriceLoading
    if (shouldShowContent && !hasTrackedViewEvent.current) {
      hasTrackedViewEvent.current = true
      setEventDefault(CHANGE_PLAN_OFFER_VIEW_GA4_EVENT)
    }
  }, [
    planComplete,
    planLoading,
    isProratePriceLoading,
    showPlanChanged,
    showChangeError,
    setEventDefault,
  ])

  useEffect(() => {
    const fetchProratePrice = async () => {
      if (!isProratePriceLoading && !proratePriceData && specialOfferPlan.get('id')) {
        setIsProratePriceLoading(true)

        try {
          const res = await getUserAccountChangePlanProratePrice({
            auth,
            nextRatePlanId: specialOfferPlan.get('id'),
          })
          setProratePriceData(fromJS(res))
        } catch (error) {
          // Set error state so we can fall back to default description
          setProratePriceData(fromJS({
            error: true,
            success: false,
            amountWithoutTax: null,
            taxAmount: null,
            targetDate: null,
            activateImmediately: false,
          }))
        } finally {
          setIsProratePriceLoading(false)
        }
      } else {
        // Reset state when not upgrading
        setProratePriceData(null)
        setIsProratePriceLoading(false)
      }
    }

    fetchProratePrice()
  }, [auth, specialOfferPlan.get('id')])

  const renderSuccessState = () => {
    return (
      <PriceSecured setEventDefault={setEventDefault} />
    )
  }

  const renderDefaultState = () => {
    return (
      <React.Fragment>
        <H1 as={HEADING_TYPES.H2} className="my-account-change-plan-promo-modal__title">
          {staticText.get('title')}
        </H1>
        <div className="my-account-change-plan-promo-modal__intro">
          <p className="my-account-change-plan-promo-modal__intro-text">
            {staticText.get('introText')}
          </p>
          <p className="my-account-change-plan-promo-modal__highlight-text">
            {staticText.get('secureRateBefore')} <span className="my-account-change-plan-promo-modal__highlight-price">{initialPrice}</span> {staticText.get('rateBeforeGone')}
          </p>
          <CountdownTimer staticText={staticText} />
        </div>
        <OfferCard
          {...props}
          staticText={staticText}
          plan={specialOfferPlan}
          planLoading={planLoading}
          planComplete={planComplete}
          initialPrice={initialPrice}
          monthlyPrice={monthlyPrice}
          recurringPrice={recurringPrice}
          onClickAcceptOffer={() => {
            setShowAcceptOfferModal(true)
            onClickChangePlanConfirm()
          }}
          currentPlan={currentPlan}
          isProcessing={isProcessing}
        />
        <Button
          type={BUTTON_TYPES.LINK}
          className="my-account-change-plan-promo-modal__other-options"
          onClick={() => {
            // Track dismissal in sessionStorage to prevent showing again
            const PROMO_MODAL_DISMISSED_KEY = 'promoModalDismissed'
            setSessionStorage(PROMO_MODAL_DISMISSED_KEY, true)
            dismissModal()
          }}
        >
          {staticText.get('seeOtherOptions')}
        </Button>
      </React.Fragment>
    )
  }

  const renderModalContent = () => {
    if (showPlanChanged) {
      return renderSuccessState()
    }

    return renderDefaultState()
  }

  // Don't show loading if we have a successful plan change - prioritize success state
  // even if plans are being refetched after auth renewal
  if (!showPlanChanged && (planLoading || !planComplete || isProratePriceLoading)) {
    return (
      <Loading staticText={staticText} />
    )
  }

  return (
    <div className="my-account-change-plan-promo-modal">
      {renderModalContent()}
      {showAcceptOfferModal && (
        <AcceptOfferModal
          staticText={staticText}
          setShowAcceptOfferModal={setShowAcceptOfferModal}
          showPlanChanged={showPlanChanged}
          isProcessing={isProcessing}
        />
      )}
    </div>
  )
}

MyAccountChangePlanPromoModal.propTypes = {
  staticText: ImmutablePropTypes.map.isRequired,
  dismissModal: PropTypes.func.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  plans: ImmutablePropTypes.map.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  resetAccountChangePlanData: PropTypes.func.isRequired,
  setPlanChangeSelected: PropTypes.func.isRequired,
  setEventDefault: PropTypes.func.isRequired,
}

export default compose(
  connectRedux(
    (state) => ({
      staticText: state.staticText.getIn(['data', 'myAccountChangePlanPromoModal', 'data']),
      user: state.user,
      auth: state.auth,
      userAccount: state.userAccount,
      plans: state.plans,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        dismissModal: actions.dialog.dismissModal,
        resetAccountChangePlanData: actions.userAccount.resetAccountChangePlanData,
        setPlanChangeSelected: actions.plans.setPlanChangeSelected,
        setEventDefault: actions.eventTracking.setEventDefault,
      }
    },
  ),
)(MyAccountChangePlanPromoModal)
